import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

/**
 * State carried by the approval-motivo modal. Shared between Nueva
 * Cotización and the Detalle Venta editor so both flows surface the
 * same UX when a discount crosses an approval-required threshold.
 */
export type AuthDialogState = {
  lineKey: string;
  articulo: string;
  descuento: number;
  motivo: string;
  /** Pre-edit values to restore if user cancels. */
  previousDescuento: number;
  previousMotivo: string;
  /** Suggested motivo from evaluateLineDiscount (shown as placeholder). */
  suggested: string;
};

export function DescuentoAprobacionDialog({
  state,
  onMotivoChange,
  onConfirm,
  onCancel,
  usadoWarning = false,
}: {
  state: AuthDialogState | null;
  onMotivoChange: (next: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** MX Nova only — true when the venta with this discount applied
   *  would land below the cost+commission+IVA threshold. Renders an
   *  extra red advertencia panel inside the dialog body so the seller
   *  sees it here too (the editor's own banner is hidden under this
   *  dialog while it's open). Default false so AR / non-Nova callers
   *  don't need to pass anything. */
  usadoWarning?: boolean;
}) {
  const open = state !== null;
  const canConfirm = Boolean(state?.motivo.trim());
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent hideClose className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Aprobación requerida</DialogTitle>
          <DialogDescription>
            Este descuento requiere aprobación. Ingresá un motivo para que
            quede registrado en la solicitud.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {usadoWarning && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
              <span className="font-semibold uppercase tracking-wide text-rose-700">
                ⚠ Advertencia ·
              </span>{' '}
              Con este descuento la venta queda por debajo del costo
              contable + comisión + IVA y se registrará como{' '}
              <strong>usado</strong>.
            </div>
          )}
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-foreground">{state?.articulo ?? ''}</span>
              <span className="rounded-md bg-fuchsia-100 px-2 py-0.5 text-xs font-semibold text-fuchsia-700">
                Descuento: {state?.descuento ?? 0}%
              </span>
            </div>
            {state?.suggested && (
              <p className="mt-2 text-xs text-muted-foreground">
                Motivo sugerido: <span className="italic">{state.suggested}</span>
              </p>
            )}
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Motivo
            </span>
            <textarea
              autoFocus
              rows={4}
              value={state?.motivo ?? ''}
              onChange={(e) => onMotivoChange(e.target.value)}
              placeholder={state?.suggested || 'Ingresar motivo...'}
              className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm shadow-sm focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            />
          </label>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!canConfirm}
            onClick={onConfirm}
            className="bg-foreground text-background hover:bg-foreground/90"
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
